import {
  useAbsatzUmsatzZielCreateOrUpdate,
  useAbsatzzielCreateOrUpdate,
  useUmsatzzielCreateOrUpdate,
} from '@/domain/controlling/hooks';
import {
  CreateOrUpdateAbsatzUmsatzZielMutation,
  CreateOrUpdateAbsatzzielMutation,
  CreateOrUpdateUmsaatzzielMutation,
  Currency,
} from '@/gql/graphql';
import { maskitoParseNumber } from '@maskito/kit';
import { MouseEvent } from 'react';
import { useRouteParams } from './useRouteParams';
import { useTagebuchEinstellungen } from '@/domain/controlling/hooks/useTagebuchEinstellungen';
import { useDialogContext } from './DialogContext';
import { FetchResult } from '@apollo/client';
import { useStandortContext } from '@/features/standortContext/StandortContext';

export type MutationResult =
  | FetchResult<CreateOrUpdateUmsaatzzielMutation>
  | FetchResult<CreateOrUpdateAbsatzzielMutation>
  | FetchResult<CreateOrUpdateAbsatzUmsatzZielMutation>;

// eslint-disable-next-line max-lines-per-function
export function useFormSubmit(
  onSuccess: (result: MutationResult) => void,
  onError: (error: Error) => void,
) {
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { searchParams } = useRouteParams();
  const { umsatzplanungAnzeige } = useTagebuchEinstellungen(standortId);
  const { zieleForm, dispatch } = useDialogContext();
  const { formValues } = zieleForm;

  const [createOrUpdateUmsatzZiel] = useUmsatzzielCreateOrUpdate();
  const [createOrUpdateAbsatzZiel] = useAbsatzzielCreateOrUpdate();
  const [createOrUpdateAbsatzUmsatzZiel] = useAbsatzUmsatzZielCreateOrUpdate();

  function handleUpdateUmsatzZielSubmit(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    dispatch({ type: 'submit' });
    createOrUpdateUmsatzZiel({
      createOrUpdateUmsatzzielInput: {
        standortId,
        jahr: searchParams.jahr,
        monat: searchParams.monat,
        umsatzziel: formValues.umsatzZiel
          ? {
              amount: maskitoParseNumber(formValues.umsatzZiel, ','),
              currency: Currency.Eur,
            }
          : null,
        umsatzzielProduktivitaet: formValues.umsatzZielproduktivitaet
          ? {
              amount: maskitoParseNumber(
                formValues.umsatzZielproduktivitaet,
                ',',
              ),
              currency: Currency.Eur,
            }
          : null,
      },
    })
      .then((result) => onSuccess(result))
      .catch(onError);
  }

  function handleUpdateAbsatzZielSubmit(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    dispatch({ type: 'submit' });
    createOrUpdateAbsatzZiel({
      createOrUpdateAbsatzzielInput: {
        standortId,
        jahr: searchParams.jahr,
        monat: searchParams.monat,
        absatzziel: formValues.absatzZiel
          ? maskitoParseNumber(formValues.absatzZiel, ',')
          : null,
        absatzzielProduktivitaet: formValues.absatzZielproduktivitaet
          ? maskitoParseNumber(formValues.absatzZielproduktivitaet, ',')
          : null,
      },
    })
      .then(onSuccess)
      .catch(onError);
  }

  function handleUpdateAbsatzUmsatzZielSubmit(
    event: MouseEvent<HTMLButtonElement>,
  ) {
    event.preventDefault();
    dispatch({ type: 'submit' });
    createOrUpdateAbsatzUmsatzZiel({
      createOrUpdateUmsatzZielInput: {
        standortId,
        jahr: searchParams.jahr,
        monat: searchParams.monat,
        umsatzziel: formValues.umsatzZiel
          ? {
              amount: maskitoParseNumber(formValues.umsatzZiel, ','),
              currency: Currency.Eur,
            }
          : null,
        umsatzzielProduktivitaet: formValues.umsatzZielproduktivitaet
          ? {
              amount: maskitoParseNumber(
                formValues.umsatzZielproduktivitaet,
                ',',
              ),
              currency: Currency.Eur,
            }
          : null,
      },
      createOrUpdateAbsatzZielInput: {
        standortId,
        jahr: searchParams.jahr,
        monat: searchParams.monat,
        absatzziel: formValues.absatzZiel
          ? maskitoParseNumber(formValues.absatzZiel, ',')
          : null,
        absatzzielProduktivitaet: formValues.absatzZielproduktivitaet
          ? maskitoParseNumber(formValues.absatzZielproduktivitaet, ',')
          : null,
      },
    })
      .then(onSuccess)
      .catch(onError);
  }

  if (umsatzplanungAnzeige === 'ABSATZ_PRODUKTIVITAET') {
    return { handleSubmit: handleUpdateAbsatzZielSubmit };
  } else if (umsatzplanungAnzeige === 'UMSATZ_ABSATZ_PRODUKTIVITAET') {
    return { handleSubmit: handleUpdateAbsatzUmsatzZielSubmit };
  }
  return { handleSubmit: handleUpdateUmsatzZielSubmit };
}
